import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsManagedByVtex } from '~/modules/retailMedia/hooks/useIsManagedByVtex'

import { buildCheckingAccountsFromInsertionOrder } from '../helpers/buildCheckingAccountsFromInsertionOrder'
import { useCampaignForm } from './useCampaignForm'

type CampaignCreateBody = Record<string, unknown>

/**
 * Validates linked Insertion Order (when ads network) and attaches
 * `checking_accounts` to the create campaign payload.
 */
export const usePrepareCampaignCreateBody = () => {
  const { t } = useTranslation()
  const { isManagedByVtex } = useIsManagedByVtex()
  const { linkedInsertionOrder, setLinkedInsertionOrderError } =
    useCampaignForm()

  const isLinkedInsertionOrderReady = useMemo(() => {
    if (!isManagedByVtex) {
      return true
    }

    if (!linkedInsertionOrder) {
      return false
    }

    return (
      buildCheckingAccountsFromInsertionOrder(linkedInsertionOrder).length > 0
    )
  }, [isManagedByVtex, linkedInsertionOrder])

  const validateLinkedInsertionOrder = useCallback((): boolean => {
    if (!isManagedByVtex) {
      return true
    }

    if (!linkedInsertionOrder) {
      setLinkedInsertionOrderError(t('rm:campaign.insertionOrder.required'))
      return false
    }

    const accounts =
      buildCheckingAccountsFromInsertionOrder(linkedInsertionOrder)

    if (!accounts.length) {
      setLinkedInsertionOrderError(
        t('rm:campaign.insertionOrder.missingCheckingAccount')
      )
      return false
    }

    return true
  }, [isManagedByVtex, linkedInsertionOrder, setLinkedInsertionOrderError, t])

  const prepareCampaignCreateBody = useCallback(
    <T extends CampaignCreateBody>(body: T): T | null => {
      if (!validateLinkedInsertionOrder()) {
        return null
      }

      if (!isManagedByVtex || !linkedInsertionOrder) {
        return body
      }

      return {
        ...body,
        checking_accounts:
          buildCheckingAccountsFromInsertionOrder(linkedInsertionOrder)
      }
    },
    [isManagedByVtex, linkedInsertionOrder, validateLinkedInsertionOrder]
  )

  return {
    prepareCampaignCreateBody,
    validateLinkedInsertionOrder,
    isLinkedInsertionOrderReady
  }
}
